import React, { useState } from 'react';
import SVGImage from '../layout/SVGImage';
import { Divider } from '../common/Divider';
import LoginSubmitButton from '../common/SubmitButton';

import Dropdown from '../forecast/Dropdown';
import { post, patch } from '../../utils/fetch';
import { InitiateAuthResponse } from '@aws-sdk/client-cognito-identity-provider';
import { encryptData } from '../../utils/security';
import { useTranslation } from 'react-i18next';
import { PowerOutageScheduleErrorMessage } from '../plant-overview/PowerOutageScheduleErrorMessage';
import { GridusException } from '@/exceptions/gridusException';
import { AccountManagementErrorWrapper } from './AccountManagementErrorWrapper';
import { ForecastAlert } from '../forecast/ForecastAlert';
import { ModalTypeEnum, UserRoleEnum } from '@/pages/AccountManagementPage';

interface AccountManagementInsertModalProps {
  testId?: string;
  className?: string;
  initialUserData: InitialUser | undefined;
  onReturned: () => void;
  type: number;
}

export const AccountManagementInsertModal: React.FC<
  AccountManagementInsertModalProps
> = ({ testId, onReturned, className, initialUserData, type }) => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<string>(
    t('addAccountModal.rolePlaceholder')
  );
  const [loading, setLoading] = useState(false);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const isUpdate = type === ModalTypeEnum.Update;
  const isCreatedBySuperAdmin = type === ModalTypeEnum.CreatedBySuperAdmin;

  const [user, setUser] = useState<{
    userName: AccountModalData;
    email: AccountModalData;
    companyCode: AccountModalData;
    companyName: AccountModalData;
    companyContact: AccountModalData;
    role: AccountModalData;
    siteId: AccountModalData;
  }>({
    userName: {
      value: initialUserData?.name,
      isError: false,
      message: '잘못된 이름입니다.',
    },
    email: {
      value: initialUserData?.email,
      isError: false,
      message: '잘못된 이메일입니다.',
    },
    companyCode: {
      value: initialUserData?.companyCode,
      isError: false,
      message: '잘못된 회사코드입니다.',
    },
    companyName: {
      value: initialUserData?.companyName,
      isError: false,
      message: '잘못된 회사명입니다.',
    },
    companyContact: {
      value: initialUserData?.customContact,
      isError: false,
      message: '잘못된 전화번호입니다.',
    },
    role: {
      value: isUpdate ? initialUserData?.customRole : undefined,
      isError: false,
      message: '역할을 선택하지 않았습니다.',
    },
    siteId: {
      value: initialUserData?.customSiteId,
      isError: false,
      message: '잘못된 siteId 권한입니다.',
    },
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Validation
      let isError = false;
      Object.entries(user).forEach(([key, data]) => {
        if (key != 'companyName' && !data.value) {
          // companyName 을 제외한 값들의 대한 검증
          setUser((prev) => ({
            ...prev,
            [key]: {
              ...prev[key as keyof typeof user],
              isError: true,
            },
          }));
          isError = true;
        }
      });

      if (isError) throw new GridusException('data validation error');

      if (!isUpdate) {
        let requestBody;
        if (isCreatedBySuperAdmin) {
          requestBody = await encryptData(
            JSON.stringify({
              username: user.userName.value,
              email: user.email.value,
              companyCode: user.companyCode.value,
              companyContact: user.companyContact.value,
              role: user.role.value,
              siteId: user.siteId.value,
            }),
            'userInfo'
          );
        } else {
          // Create :  Admin
          requestBody = await encryptData(
            JSON.stringify({
              username: user.userName.value,
              email: user.email.value,
              companyCode: user.companyCode.value,
              companyContact: user.companyContact.value,
              role: user.role.value,
              siteId: initialUserData?.customSiteId,
            }),
            'userInfo'
          );
        }

        const response = await post<{ data: InitiateAuthResponse }>(
          '/auth/admin/user',
          {
            body: JSON.stringify(requestBody),
          }
        );

        if (response.status === 'success') {
          onReturned();
        } else {
          setIsSubmissionError(true);
        }
      } else {
        // Update
        const userInfo = {
          email: user.email.value,
          userAttributes: [
            {
              Name: 'custom:username',
              Value: user.userName.value,
            },
            {
              Name: 'custom:companyContact',
              Value: user.companyContact.value,
            },
          ],
        };

        const requestBody = await encryptData(
          JSON.stringify(userInfo),
          'userInfo'
        );

        const response = await patch<{ data: InitiateAuthResponse }>(
          '/auth/user',
          {
            body: JSON.stringify(requestBody),
          }
        );

        if (response.status === 'success') {
          onReturned();
        } else {
          setIsSubmissionError(true);
        }
      }
    } catch (error) {
      console.error('Error occurred during create account: ', error);
      setIsSubmissionError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`flex w-[595px] flex-col items-end ${className}`}
      data-testid={testId}
    >
      {loading && (
        <div className="fixed left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-gray-500 opacity-50">
          <SVGImage name="dropdown-spinner" />
        </div>
      )}
      <SVGImage
        className="mb-[15px] cursor-pointer"
        testId={`${testId}-close-button`}
        name="cancle"
        onClick={onReturned}
      />
      <div className="w-full bg-white p-[40px] pt-[35px]">
        <p
          className={`noto-semibold-4 text-dark-3`}
          data-testid={`${testId}-title`}
        >
          {isUpdate
            ? t('addAccountModal.updateAccountTitle')
            : t('addAccountModal.addAccountTitle')}
        </p>
        <Divider className="mb-[14px] mt-[17px] bg-[#D6DEE4]"></Divider>
        <p className={`noto-13-semibold mb-[5px] leading-[23px] text-dark-3`}>
          {t('addAccountModal.accountInfoTitle')}
        </p>
        <div className="mt-[26px] flex">
          <div>
            <p className={`noto-12-normal mb-[8px] leading-[23px] text-dark-6`}>
              {t('addAccountModal.nameTitle')}
            </p>
            <div className="flex">
              <input
                value={user.userName.value}
                onChange={(e) =>
                  setUser((prev) => ({
                    ...prev,
                    userName: {
                      ...prev.userName,
                      value: e.target.value,
                      isError: false,
                    },
                  }))
                }
                placeholder={t('addAccountModal.namePlaceholder')}
                className={`noto-12-normal h-[36px] w-[250px] rounded-[5px] bg-[#F3F6F8] pl-[10px] text-[#333] focus:outline-none ${user.userName.isError ? 'border border-red-500' : ''}`}
              />
            </div>
            {user.userName.isError && (
              <PowerOutageScheduleErrorMessage
                className="mt-[10px]"
                text={user.userName.message}
              />
            )}
          </div>
          <div className="ml-[15px]">
            <p className={`noto-12-normal mb-[8px] leading-[23px] text-dark-6`}>
              {t('addAccountModal.emailTitle')}
            </p>
            <div className="flex">
              <input
                value={user.email.value}
                onChange={(e) => {
                  const value = e.target.value;
                  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (pattern.test(value)) {
                    // pass
                    setUser((prev) => ({
                      ...prev,
                      email: {
                        ...prev.email,
                        value: value,
                        isError: false,
                      },
                    }));
                  } else {
                    // fail
                    setUser((prev) => ({
                      ...prev,
                      email: {
                        ...prev.email,
                        value: value,
                        isError: true,
                      },
                    }));
                  }
                }}
                placeholder={t('addAccountModal.emailPlaceholder')}
                className={`noto-12-normal h-[36px] w-[250px] rounded-[5px] bg-[#F3F6F8] pl-[10px] focus:outline-none ${isUpdate ? 'bg-[#E4E9ED] text-[#98A2AB]' : 'bg-[#F3F6F8] text-[#3F4C5A]'} ${user.email.isError ? 'border border-red-500' : ''}`}
                disabled={isUpdate}
              />
            </div>
            {user.email.isError && (
              <PowerOutageScheduleErrorMessage
                className="mt-[10px]"
                text={user.email.message}
              />
            )}
          </div>
        </div>
        <div className="mt-[16px]">
          <p className={`noto-12-normal mb-[8px] leading-[23px] text-dark-6`}>
            {t('addAccountModal.roleTitle')}
          </p>
          <div className="flex">
            <AccountManagementErrorWrapper
              isError={user.role.isError}
              errorMessage={user.role.message}
            >
              {isUpdate ? (
                // Update
                <input
                  value={UserRoleEnum[Number(initialUserData?.customRole)]}
                  className={`noto-12-normal h-[36px] w-[250px] rounded-[5px] bg-[#F3F6F8] pl-[10px] text-[#98A2AB] focus:outline-none ${user.role.isError ? 'border border-red-500' : ''}`}
                  disabled={true}
                />
              ) : (
                // Create
                <Dropdown
                  testId="role-dropdown-button"
                  itemTestId="role-dropdown-items"
                  items={
                    initialUserData?.customRole === String(UserRoleEnum.Admin)
                      ? [UserRoleEnum[0]] // admin
                      : [UserRoleEnum[0], UserRoleEnum[1]] // superAdmin
                  }
                  selectedItem={selectedRole}
                  className={`noto-12-normal h-[36px] w-[250px] rounded-[5px] pl-[10px] pr-[8px] pt-[8px] ${isUpdate ? 'bg-[#E4E9ED] text-[#98A2AB]' : 'bg-[#F3F6F8] text-[#3F4C5A]'} ${user.role.isError ? 'border border-red-500' : ''}`}
                  onSelected={(text) => {
                    let roleNumber: string;
                    switch (text) {
                      case UserRoleEnum[0]:
                        roleNumber = String(UserRoleEnum.User);
                        break;
                      case UserRoleEnum[1]:
                        roleNumber = String(UserRoleEnum.Admin);
                        break;
                      case UserRoleEnum[2]:
                        roleNumber = String(UserRoleEnum.SuperAdmin);
                        break;
                    }
                    setSelectedRole(text);
                    setUser((prev) => ({
                      ...prev,
                      role: {
                        ...prev.role,
                        value: roleNumber,
                        isError: false,
                      },
                    }));
                  }}
                ></Dropdown>
              )}
            </AccountManagementErrorWrapper>
          </div>
        </div>
        <Divider className="mb-[14px] mt-[19px] bg-[#D6DEE4]"></Divider>
        <p
          className={`noto-13-semibold leading-[23px] text-dark-3 [@media(max-height:1000px)]:mb-[26px]`}
        >
          {t('addAccountModal.companyInfoTitle')}
        </p>
        <div className="flex">
          <AccountManagementErrorWrapper
            isError={user.companyCode.isError}
            errorMessage={user.companyCode.message}
          >
            {isCreatedBySuperAdmin ? (
              // super admin create
              <div>
                <p
                  className={`noto-12-normal mb-[8px] leading-[23px] text-dark-6`}
                >
                  {t('addAccountModal.companyCodeTitle')}
                </p>
                <div className="flex">
                  <input
                    value={user.companyCode.value}
                    placeholder={t('addAccountModal.companyCodePlaceholder')}
                    className={`noto-12-normal h-[36px] w-[250px] rounded-[5px] bg-[#F3F6F8] pl-[10px] text-[#3F4C5A] focus:outline-none`}
                    onChange={(e) => {
                      const value = e.target.value;
                      const pattern = /^\d+-\d+-\d+$/;

                      if (pattern.test(value)) {
                        // Pass
                        setUser((prev) => ({
                          ...prev,
                          companyCode: {
                            ...prev.companyCode,
                            value: value,
                            isError: false,
                          },
                        }));
                      } else {
                        // Fail
                        setUser((prev) => ({
                          ...prev,
                          companyCode: {
                            ...prev.companyCode,
                            value: value,
                            isError: true,
                          },
                        }));
                      }
                    }}
                  />
                </div>
              </div>
            ) : (
              // admin create
              <div>
                <p
                  className={`noto-12-normal mb-[8px] leading-[23px] text-dark-6`}
                >
                  {t('addAccountModal.companyTitle')}
                </p>
                <div className="flex">
                  <input
                    value={user.companyName.value}
                    placeholder={t('addAccountModal.companyPlaceholder')}
                    className={`noto-12-normal h-[36px] w-[250px] rounded-[5px] bg-[#F3F6F8] pl-[10px] text-[#98A2AB] focus:outline-none`}
                    disabled={true}
                  />
                </div>
              </div>
            )}
          </AccountManagementErrorWrapper>
          <div className="ml-[15px]">
            <p className={`noto-12-normal mb-[8px] leading-[23px] text-dark-6`}>
              {t('addAccountModal.contactTitle')}
            </p>
            <div className="flex">
              <input
                value={user.companyContact.value}
                onChange={(e) => {
                  const value = e.target.value;
                  const pattern = /^\d{2,3}-\d{3,4}-\d{4}$/;

                  if (pattern.test(value)) {
                    // Pass
                    setUser((prev) => ({
                      ...prev,
                      companyContact: {
                        ...prev.companyContact,
                        value: value,
                        isError: false,
                      },
                    }));
                  } else {
                    // Fail
                    setUser((prev) => ({
                      ...prev,
                      companyContact: {
                        ...prev.companyContact,
                        value: value,
                        isError: true,
                      },
                    }));
                  }
                }}
                placeholder={t('addAccountModal.contactPlaceholder')}
                className={`text-[#3F4C5A]} noto-12-normal h-[36px] w-[250px] rounded-[5px] bg-[#F3F6F8] pl-[10px] focus:outline-none ${user.companyContact.isError ? 'border border-red-500' : ''}`}
              />
            </div>
            {user.companyContact.isError && (
              <PowerOutageScheduleErrorMessage
                className="mt-[10px]"
                text={user.companyContact.message}
              />
            )}
          </div>
        </div>
        {isCreatedBySuperAdmin && (
          <div className="mt-[16px]">
            <p className={`noto-12-normal mb-[8px] leading-[23px] text-dark-6`}>
              {t('addAccountModal.siteIdTitle')}
            </p>
            <div className="flex">
              <input
                className={`noto-12-normal h-[36px] w-[250px] rounded-[5px] bg-[#F3F6F8] pl-[10px] text-[#3F3C5A] focus:outline-none ${user.siteId.isError ? 'border border-red-500' : ''}`}
                placeholder={t('addAccountModal.siteIdPlaceholder')}
                onChange={(e) => {
                  const value = e.target.value;
                  const pattern = /^([^\s,]+)(,[^\s,]+)*$/;

                  if (pattern.test(value)) {
                    setUser((prev) => ({
                      ...prev,
                      siteId: {
                        ...prev.siteId,
                        value: value,
                        isError: false,
                      },
                    }));
                  } else {
                    setUser((prev) => ({
                      ...prev,
                      siteId: {
                        ...prev.siteId,
                        value: value,
                        isError: true,
                      },
                    }));
                  }
                }}
              />
            </div>
          </div>
        )}
        {isCreatedBySuperAdmin && user.siteId.isError && (
          <PowerOutageScheduleErrorMessage text={user.siteId.message} />
        )}
        <Divider className="mb-[30px] mt-[30px] bg-[#3F4C5A]"></Divider>
        <div className="flex items-center justify-end">
          <div>
            <LoginSubmitButton
              text="확인"
              testId="add-account-modal-submit-button"
              disabled={false}
              submitted={false}
              onClick={handleSubmit}
            ></LoginSubmitButton>
            {isSubmissionError &&
              (isUpdate ? (
                <ForecastAlert
                  message="계정 수정에 실패했습니다."
                  className="mt-[10px]"
                />
              ) : (
                <ForecastAlert
                  message="계정 추가에 실패했습니다."
                  className="mt-[10px]"
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
